import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import SocialIcons from "@/components/SocialIcons";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/specialties", label: "Specialties" },
  { to: "/shop", label: "Shop" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

const BOOKING_URL = "https://sophia-dinwiddie-donald.clientsecure.me/request/clinician";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-surface/85 backdrop-blur-xl border-b border-sage/20 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
          : "bg-surface/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
        <Link
          to="/"
          data-testid="logo-link"
          className="font-display text-2xl md:text-[26px] font-semibold text-forest leading-none"
          onClick={() => setOpen(false)}
        >
          Seven Pillars <span className="text-terracotta">of Life</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors link-underline ${
                  isActive ? "text-forest font-semibold" : "text-ink/70 hover:text-forest"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <SocialIcons variant="compact" size={16} className="text-forest" />
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-book-btn"
            className="inline-flex items-center bg-forest text-bone rounded-full px-6 py-3 text-sm font-medium hover:bg-[#5D4B85] transition-colors duration-300"
          >
            Book Consultation
          </a>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-forest"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-surface border-t border-sage/20" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base tracking-wide ${isActive ? "text-forest font-semibold" : "text-ink/80"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-book-btn"
              className="mt-2 inline-flex justify-center bg-forest text-bone rounded-full px-6 py-3 text-sm font-medium"
            >
              Book Consultation
            </a>
            <div className="mt-4 flex justify-center text-forest">
              <SocialIcons variant="compact" size={16} />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
